"use client";

import {
	Divider,
	Input,
	Radio,
	RadioGroup,
	Select,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import Link from "next/link";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { ModularContentCancellationFormFragment, SiteLocale } from "src/__generated__/cms-schema.codegen";
import { FormErrorMessageI18n } from "src/components/FormErrorMessageI18n/FormErrorMessageI18n";
import { EmailInput } from "src/components/Inputs/EmailInput";
import { RadioStack } from "src/components/RadioStack/RadioStack";
import { Textarea } from "src/components/Textarea/Textarea";
import { FormControl, FormLabel } from "src/components/forms";
import { Button } from "src/components/ui/button";
import { useTranslatedString } from "src/i18n/hooks";
import { Checklist, EmailCheckmark } from "src/icons";
import { useSiteSettings } from "src/lib/hooks";
import { useErrorToast } from "src/lib/toasts";
import { z } from "src/lib/zod";
import { useSendEmail } from "src/queries/emil/email";
import { TypeOf } from "zod";

type CancellationFormProps = ModularContentCancellationFormFragment;

const cancellationSchema = z.object({
	salutation: z.enum(["mr", "mrs", "diverse"]),
	title: z.string().optional(),
	firstName: z.string().min(1),
	lastName: z.string().min(1),
	contractNumber: z.string().min(1),
	contractType: z.string().min(1),
	email: z.string().email(),
	phone: z.string().optional(),
});

type CancellationFormValues = TypeOf<typeof cancellationSchema>;

type Step = "form" | "confirmation" | "success";

const SummaryRow: React.FC<{ label: string; value: string }> = ({
	label,
	value
}) => (
	<div className="flex justify-between py-3 border-t border-border first:border-0 ">
		<span>{label}</span>
		<span className="text-right">{value}</span>
	</div>
);

const successTextRenderer = (email: string) => ({
	br: <br />,
	email
});

export const CancellationForm: React.FC<CancellationFormProps> = ({
	formTitle,
	formSubtitle,
	confirmationTitle,
	confirmationSubtitle,
	successTitle,
	surveyUrl
}) => {
	const { locale } = useSiteSettings();
	const t = useTranslatedString();
	const translateSalutation = (value: "mr" | "mrs" | "diverse") =>
		value === "mr"
			? t("cancel.form.salutation.mr")
			: value === "mrs"
				? t("cancel.form.salutation.mrs")
				: undefined;
	const errorToast = useErrorToast();
	const sendEmail = useSendEmail();
	const [step, setStep] = useState<Step>("form");
	const [formValues, setFormValues] =
		useState<CancellationFormValues | null>(null);

	const form = useForm<CancellationFormValues>({
		resolver: zodResolver(cancellationSchema),
		mode: "onTouched"
	});

	const {
		register,
		control,
		formState: { errors, isValid },
		handleSubmit,
	} = form;

	const goToConfirmation = handleSubmit((data) => {
		setFormValues(data);
		setStep("confirmation");

		window.scrollTo(0, 0);
	});

	const sendEmailAndGoToSuccess = async () => {
		// formValues can't actually be null at this point, but TS complained
		if (formValues === null) {
			return;
		}

		sendEmail.mutate(
			{
				emailTemplateSlug: locale === SiteLocale.de
					? "withdrawal_confirmation_DE"
					: "withdrawal_confirmation_EN",
				emailSubject: t("cancel.email.subject"),
				payload: {
					CDATE: format(new Date(), "dd.MM.yyyy'; 'HH:mm' Uhr'"),
					SALUTATION:
						translateSalutation(formValues.salutation) ?? "",
					TITLE: formValues.title ?? "",
					FNAME: formValues.firstName,
					LNAME: formValues.lastName,
					EMAIL: formValues.email,
					PHONE: formValues.phone ?? "",
					POLICYNO: formValues.contractNumber,
					POLICY: formValues.contractType,
				},
			},
			{
				onError: () => {
					errorToast(t("cancel.confirm.error"), {
						id: "cancellation-error",
					});
				},
				onSuccess: () => {
					setStep("success");
					window.scrollTo(0, 0);
				},
			},
		);
	};

	if (step === "success" && formValues) {
		return (
			<div className="flex flex-col sm:px-0 sm:max-w-10/12 md:max-w-5/10 px-6 pb-8 mx-auto">
				<h2 className="heading-2 text-center mb-8">
					{successTitle}
				</h2>
				<EmailCheckmark w={18} h={18} className="mx-auto mt-4 mb-8" />
				<div className="rounded-2xl border border-border bg-(--muted) mt-8 p-6 mb-16">
					{t("cancel.success.message", successTextRenderer(formValues.email))}
				</div>
				{surveyUrl && (
					<div>
						<div className="text-lg leading-6 mb-16">
							<p className="text-endeavour-500 font-semibold mb-2">
								{t("cancel.success.survey.title")}
							</p>
							<p className="text-endeavour-800">
								{t("cancel.success.survey.body")}
							</p>
						</div>
						<Button asChild className="w-full mb-4">
							<a
								href={surveyUrl}
								target="_blank"
								rel="noreferrer"
							>
								{t("cancel.success.nav.survey")}
							</a>
						</Button>
					</div>
				)}
				<Link href="/">
					<Button variant="tertiary" className="w-full">
						{t("cancel.success.nav.home")}
					</Button>
				</Link>
			</div>
		);
	}

	if (step === "confirmation" && formValues) {
		const summaryRows = [
			{
				label: t("cancel.confirm.summary.name"),
				value: [
					translateSalutation(formValues.salutation),
					formValues.title,
					formValues.firstName,
					formValues.lastName,
				].filter(Boolean).join(" "),
			},
			{
				label: t("cancel.form.contractNumber.label"),
				value: formValues.contractNumber,
			},
			{
				label: t("cancel.form.contractType.label"),
				value: formValues.contractType,
			},
			{
				label: t("cancel.confirm.summary.email"),
				value: formValues.email,
			},
			{
				label: t("cancel.confirm.summary.phone"),
				value: formValues.phone === undefined
					|| formValues.phone.length === 0
					? t("cancel.confirm.summary.unavailable")
					: formValues.phone,
			},
		];

		return (
			<div className="flex flex-col sm:px-0 sm:max-w-10/12 md:max-w-5/10 px-6 pb-8 mx-auto">
				<h2 className="heading-2 text-center mb-4">
					{confirmationTitle}
				</h2>
				<p className="text-endeavour-800 text-center text-lg">
					{confirmationSubtitle}
				</p>
				<div className="rounded-2xl border border-border bg-(--muted) mt-8 p-6 mb-16">
					<div className="flex items-center justify-between mb-4">
						<h3 className="text-endeavour-500 text-xl font-semibold">
							{t("cancel.confirm.summary.title")}
						</h3>
						<Checklist w={6} h={6} />
					</div>
					<div>
						{summaryRows.map(({ label, value }) => (
							<SummaryRow key={label} label={label} value={value} />
						))}
					</div>
				</div>
				<Button
					className="w-full mb-4"
					onClick={sendEmailAndGoToSuccess}
					disabled={sendEmail.isPending}
				>
					{t("cancel.confirm.nav.submit")}
				</Button>
				<Button
					variant="tertiary"
					className="w-full"
					onClick={() => {
						setStep("form")
						window.scrollTo(0, 0);
					}}
					disabled={sendEmail.isPending}
				>
					{t("cancel.confirm.nav.back")}
				</Button>
			</div>
		);
	}

	return (
		<div className="sm:px-0 sm:max-w-10/12 md:max-w-5/10 px-6 pb-8 mx-auto">
			<FormProvider {...form}>
				<form onSubmit={goToConfirmation} className="flex flex-col">
					<div className="text-center">
						<h2 className="heading-2 p-4">
							{formTitle}
						</h2>
						<p className="text-endeavour-800 text-lg">
							{formSubtitle}
						</p>
					</div>

					<div className="flex flex-col gap-8 mt-16">
						<FormControl isInvalid={Boolean(errors.salutation)}>
							<FormLabel>
								{t("cancel.form.salutation.label")}
							</FormLabel>
							<Controller
								control={control}
								name="salutation"
								render={({ field: { onChange, value } }) => (
									<RadioGroup onChange={onChange} value={value}>
										<RadioStack>
											<Radio value="mrs">
												{t("cancel.form.salutation.mrs")}
											</Radio>
											<Radio value="mr">
												{t("cancel.form.salutation.mr")}
											</Radio>
											<Radio value="diverse">
												{t("cancel.form.salutation.diverse")}
											</Radio>
										</RadioStack>
									</RadioGroup>
								)}
							/>
							<FormErrorMessageI18n error={errors.salutation} />
						</FormControl>

						<FormControl isInvalid={Boolean(errors.title)}>
							<FormLabel>
								{t("cancel.form.title.label")}
							</FormLabel>
							<Select {...register("title")}>
								{/* eslint-disable react/jsx-no-literals */}
								<option value="">-</option>
								<option value="Dr.">Dr.</option>
								<option value="Prof.">Prof.</option>
								<option value="Prof. Dr.">Prof. Dr.</option>
								{/* eslint-enable react/jsx-no-literals */}
							</Select>
							<FormErrorMessageI18n error={errors.title} />
						</FormControl>

						<FormControl isInvalid={Boolean(errors.firstName)}>
							<FormLabel>
								{t("cancel.form.firstName.label")}
							</FormLabel>
							<Input
								placeholder={
									t("cancel.form.firstName.placeholder")
								}
								{...register("firstName")}
							/>
							<FormErrorMessageI18n error={errors.firstName} />
						</FormControl>

						<FormControl isInvalid={Boolean(errors.lastName)}>
							<FormLabel>
								{t("cancel.form.lastName.label")}
							</FormLabel>
							<Input
								placeholder={
									t("cancel.form.lastName.placeholder")
								}
								{...register("lastName")}
							/>
							<FormErrorMessageI18n error={errors.lastName} />
						</FormControl>

						<FormControl isInvalid={Boolean(errors.contractNumber)}>
							<FormLabel>
								{t("cancel.form.contractNumber.label")}
							</FormLabel>
							<Input
								placeholder={
									t("cancel.form.contractNumber.placeholder")
								}
								{...register("contractNumber")}
							/>
							<FormErrorMessageI18n error={errors.contractNumber} />
						</FormControl>

						<FormControl isInvalid={Boolean(errors.contractType)}>
							<FormLabel>
								{t("cancel.form.contractType.label")}
							</FormLabel>
							<Controller
								control={control}
								name="contractType"
								render={({ field }) => (
									<Textarea
										placeholder={
											t("cancel.form.contractType.placeholder")
										}
										{...field}
									/>
								)}
							/>
							<FormErrorMessageI18n error={errors.contractType} />
						</FormControl>

						<Divider my={2} />

						<EmailInput<typeof cancellationSchema> name="email" />

						<FormControl isInvalid={Boolean(errors.phone)}>
							<FormLabel>
								{t("cancel.form.phone.label")}
							</FormLabel>
							<Input
								type="tel"
								placeholder={
									t("cancel.form.phone.placeholder")
								}
								{...register("phone")}
							/>
							<FormErrorMessageI18n error={errors.phone} />
						</FormControl>
					</div>

					<div className="py-6 md:py-8">
						<Button
							type="submit"
							disabled={!isValid}
							className="w-full"
						>
							{t("cancel.form.nav.next")}
						</Button>
					</div>
					<p className="text-xs mt-4 text-endeavour-800 opacity-60">
						{t("cancel.form.footnote")}
					</p>
				</form>
			</FormProvider>
		</div>
	);
};

// 🔬 TBD
